import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import {
  disableSwitcher,
  enableSwitcher,
} from "../../../../redux/features/multiEntitySwitcher/multiEntitySwitcher";
import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import globalText from "../../../../utils/globalText";
import UniversalRefreshToast from "../../UniversalRefreshToast";
import OwnerAccountPopUp from "../../../Accounts/OwnerAccount/OwnerAccountPopUp";
import Loader from "../../../Loader/Index";
import useServiceChecker from "../../../ServiceChecker";
import CommonBankLogo from "../../CommonBankLogo";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonToast from "../../CommonToast";
import CardActionableButton from "../../CommonViewAllCard/CardSubComponent/CardActionableButton";
import { formatAccountNumber } from "../../FormatAccountNumber";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";

/**
 * CommonSourceAccount
 *
 * Source (owner) account card used in transaction create flows.
 * The destination account has its own component: CommonDestinationAccount.
 *
 * @param {Boolean}  props.modalOpen                - Is the owner account popup open
 * @param {Function} props.setModalOpen             - Setter for popup state
 * @param {Object}   props.txnPayLoad               - Transaction payload
 * @param {Function} props.setTxnPayLoad            - Setter for transaction payload
 * @param {Function} props.handleOnAccountSelection - (name, accNo, ifsc, id) => void
 * @param {String}   props.page                     - Pass "confirm" on the confirm page (hides edit)
 * @param {Boolean}  props.isDisabled               - Dims and blocks the whole card
 * @param {Boolean}  props.isEditDisabled           - Disables the edit button / popup opening
 * @param {Boolean}  props.isReimbursement          - Changes the select button text
 * @param {String}   props.TypeRTGS                 - Passed through to OwnerAccountPopUp
 * @param {Object}   props.currentAccountDetails    - Passed through to OwnerAccountPopUp
 * @param {String}   props.accountHeader            - Optional header override
 * @param {String}   props.accountSubHeader         - Optional sub header override
 */
const CommonSourceAccount = ({
  page,
  modalOpen,
  setModalOpen,
  txnPayLoad,
  setTxnPayLoad,
  handleOnAccountSelection,
  isDisabled,
  isEditDisabled,
  isReimbursement,
  TypeRTGS,
  currentAccountDetails,
  accountHeader,
  accountSubHeader,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ensureAll] = useServiceChecker();

  const toastIdRef = useRef(null);
  const completedRef = useRef(false);
  const requestActiveRef = useRef(false);
  const hasSeenLoadingRef = useRef(false);

  const [refreshAnimationId, setRefreshAnimationId] = useState(null);
  const [previousRefreshData, setPreviousRefreshData] = useState(null);
  const [isBalanceRefreshing, setIsBalanceRefreshing] = useState(false);

  const services = useSelector((state) => state.refreshToken.services);
  const {
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshError,
    createTransactionBankRefreshData,
  } = useSelector((state) => state.ownerAllVerification);

  const hasBalanceService =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;

  const hasAccount = Boolean(
    txnPayLoad?.ownerBankAccountNo && txnPayLoad?.ownerBankIFSC,
  );

  const shouldBeRedacted = (price) =>
    price === "xxxx.xx" ||
    isNaN(+price) ||
    price === null ||
    price === undefined;

  /* ------------------------ switcher lock while popup is open ------------------------ */
  // Previously handled inside CommonAccountModal.
  useEffect(() => {
    if (!modalOpen) return;

    dispatch(disableSwitcher());

    return () => {
      dispatch(enableSwitcher());
    };
  }, [modalOpen, dispatch]);

  /* ------------------------------- balance refresh ------------------------------- */
  const cleanupAfterRefresh = () => {
    toastIdRef.current = null;
    requestActiveRef.current = false;
    completedRef.current = false;
    hasSeenLoadingRef.current = false;
  };

  useEffect(() => {
    // Track when loading starts so we only process results of actual API calls
    if (createTransactionBankRefreshLoading && requestActiveRef.current) {
      hasSeenLoadingRef.current = true;
      if (!toastIdRef.current) {
        UniversalRefreshToast();
      }
      return;
    }

    // Ignore anything after completion or if no request was made
    if (!requestActiveRef.current || completedRef.current) return;

    // Avoid processing stale results
    if (!hasSeenLoadingRef.current) return;

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshData?.status
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);

      CommonToast({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
      });

      cleanupAfterRefresh();
      return;
    }

    if (
      !createTransactionBankRefreshLoading &&
      createTransactionBankRefreshError
    ) {
      completedRef.current = true;

      toast.dismiss(toastIdRef.current);
      CommonToast({
        text: createTransactionBankRefreshError,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });

      cleanupAfterRefresh();
    }
  }, [
    createTransactionBankRefreshLoading,
    createTransactionBankRefreshData,
    createTransactionBankRefreshError,
  ]);

  const handleRefresh = async (e, id, prevBalance) => {
    e.stopPropagation();

    if (
      !(services?.includes("VAccountBalance") || services?.includes("Admin"))
    ) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: ["VAccountBalance", "Admin"],
        },
      });
      return;
    }

    setIsBalanceRefreshing(true);

    if (requestActiveRef.current) return;

    requestActiveRef.current = true;
    completedRef.current = false;
    toastIdRef.current = null;

    setPreviousRefreshData(prevBalance);
    setRefreshAnimationId(id);

    const response = await dispatch(getCreateTransactionBalance(id));
    setIsBalanceRefreshing(false);

    if (response?.payload?.status === true) {
      const ownerAccount = response.payload?.owner_accounts?.[0];

      setTxnPayLoad((prev) => ({
        ...prev,
        ownerBankBalance: ownerAccount?.owner_account_balance,
        ownerLastUpdated:
          ownerAccount?.owner_account_balance_last_fetched_at_in_words,
      }));
    }
  };

  /* ---------------------------------- popup ---------------------------------- */
  const handleOpenModal = () => {
    if (isEditDisabled || page === "confirm") return;
    setModalOpen(true);
  };

  const handleOwnerAccountSelect = (dataItem) => {
    handleOnAccountSelection(
      dataItem?.name?.trim(),
      dataItem?.bank_acc_no?.trim(),
      dataItem?.ifsc_code?.trim(),
      dataItem?.id?.trim(),
    );
  };

  /* --------------------------------- derived ---------------------------------- */
  const balance = txnPayLoad?.ownerBankBalance;

  const isBalanceNegative =
    balance &&
    balance < GlobalConfiguration?.lengthCheckZero &&
    !(isNaN(balance) || balance === "xxxx.xx");

  const balanceClass = balance
    ? isBalanceNegative
      ? "text-failed font-semibold"
      : "text-settled font-semibold"
    : "text-text-primary font-normal";

  const balancePrefix = balance
    ? formatTotalBalance(balance, "Default").prefix
    : null;

  const balanceCaption = `${
    balance
      ? balancePrefix === "crores"
        ? `Balance in ${balancePrefix} - `
        : globalText?.BALANCE_UPDATED
      : globalText?.BALANCE_UPDATED
  } ${txnPayLoad?.ownerLastUpdated}`;

  const showTrend =
    !createTransactionBankRefreshLoading &&
    txnPayLoad?.ownerId === refreshAnimationId;

  return (
    <>
      {modalOpen && (
        <div className="fixed top-top-base left-left-base w-full-width min-h-full-height flex items-center justify-center z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <OwnerAccountPopUp
            setOpenOwner={setModalOpen}
            txnPayLoad={txnPayLoad}
            setTxnPayLoad={setTxnPayLoad}
            handleButtonClick={handleOwnerAccountSelect}
            ownerId={txnPayLoad?.owner_id}
            flow="createTxn"
            TypeAccount="InternalAccountTxn"
            type="source"
            TypeRTGS={TypeRTGS}
            currentAccountDetails={currentAccountDetails}
          />
        </div>
      )}

      <div className="w-width-full">
        <div className="flex flex-col relative rounded-border-radius-md">
          <div
            className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${
              isDisabled
                ? "opacity-light-opacity cursor-not-allowed pointer-events-none"
                : ""
            }`}
          >
            <div className="p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md">
              {page !== "confirm" && hasAccount ? (
                <div className="top-top-base right-right-base absolute">
                  <IconTearDropButton
                    handleFunction={handleOpenModal}
                    isDisabled={isEditDisabled || false}
                    isLoading={false}
                    enabledBgColor="var(--primary-nav-act)"
                    icon={ICONS?.EDIT}
                    hoverOption="white"
                    title={globalText?.EDIT}
                  />
                </div>
              ) : null}

              <div className="flex flex-col gap-gap-md">
                <div className="flex flex-col gap-gap-xs">
                  <span className="text-font-md text-text-primary font-semibold">
                    {accountHeader || globalText?.SOURCE_ACCOUNT_CAPITALIZE}
                  </span>
                  <span className="text-xs text-text-secondary">
                    {accountSubHeader || globalText?.SOURCE_ACCOUNT_DES_TEXT}
                  </span>
                </div>

                {!hasAccount ? (
                  <div>
                    <PrimaryWithIconButton
                      btnText={
                        isReimbursement
                          ? globalText?.SELECT_REIMBURSEMENT_ACCOUNT
                          : globalText?.SELECT_OWNER_ACCOUNT
                      }
                      enabledBgColor="var(--primary-nav-act)"
                      isDisabled={false}
                      handleFunction={handleOpenModal}
                      iconToBeAlign="left"
                      enabledTextColor="var(--text-primary)"
                      icon={ICONS?.SELECT_ACCOUNT}
                      fullWidthRequired={true}
                      hoverOption="white"
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-gap-sm">
                      {/* Account number */}
                      <div className="flex items-center justify-between gap-gap-xs">
                        <div className="flex flex-col w-width-full h-auto gap-gap-xs">
                          <span className="text-sm text-text-primary font-semibold">
                            {formatAccountNumber(
                              txnPayLoad?.ownerBankAccountNo,
                            )}
                          </span>
                          <span className="text-xs text-text-secondary">
                            {globalText?.ACCOUNT_NUMBER}
                          </span>
                        </div>
                        <CommonBankLogo
                          ifsc={txnPayLoad?.ownerBankIFSC}
                          variant="big"
                          type="pill"
                        />
                      </div>
                      <div className="border-t border-primary-bg"></div>
                      {/* Balance */}
                      <div className="flex flex-col Qlg:flex-row w-width-full h-auto Qlg:gap-gap-xs gap-gap-base justify-between">
                        <div className="flex flex-col Qlg:flex-row flex-1 Qlg:gap-gap-sm gap-gap-md w-width-full Qlg:w-width-md">
                          <div className="w-width-full flex justify-between">
                            <div className="flex flex-col gap-gap-xs">
                              <div className="flex items-center gap-gap-sm relative">
                                <div
                                  className={`text-left text-sm ${balanceClass}`}
                                >
                                  {shouldBeRedacted(balance) ? "" : "INR "}
                                  {(balance ?? balancePrefix === "crores")
                                    ? formatTotalBalance(balance, "crores")
                                        .amount
                                    : formatTotalBalance(balance, "Default")
                                        .amount}
                                </div>

                                {hasBalanceService && (
                                  <>
                                    {createTransactionBankRefreshLoading &&
                                    isBalanceRefreshing ? (
                                      <Loader
                                        height="var(--icon-sm)"
                                        width="var(--icon-sm)"
                                      />
                                    ) : (
                                      <div className="relative">
                                        {showTrend &&
                                          Number(balance) <
                                            Number(previousRefreshData) && (
                                            <SVGComponent
                                              url={ICONS?.ARROW_DOWN_RED_GIF}
                                              height="var(--icon-sm)"
                                            />
                                          )}

                                        {showTrend &&
                                          Number(balance) >
                                            Number(previousRefreshData) && (
                                            <SVGComponent
                                              url={ICONS?.ARROW_UP_GREEN_GIF}
                                              height="var(--icon-sm)"
                                            />
                                          )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>

                              <div className="text-left text-xs text-text-secondary whitespace-nowrap">
                                <div className="w-full max-w-full overflow-hidden">
                                  <TickerAnimation text={balanceCaption} />
                                </div>
                              </div>
                            </div>

                            {hasBalanceService && (
                              <div className="flex flex-col justify-center">
                                <CardActionableButton
                                  btnType="button"
                                  handleFunction={(e) =>
                                    handleRefresh(
                                      e,
                                      txnPayLoad?.ownerId,
                                      txnPayLoad?.ownerBankBalance,
                                    )
                                  }
                                  isDisabled={!hasBalanceService}
                                  disabledBgColor="var(--primary-nav-act)"
                                  icon={ICONS?.RELOAD_REFRESH_WHITE}
                                  hoverOption="white"
                                  title="Refresh"
                                  bgColor="var(--primary-nav-act)"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonSourceAccount;
